import { EMPTY, EMPTY_COLOR } from './types';
import type { BeadColor, BeadSizeId, Pattern, StitchId } from './types';

export const MAX_DIM = 400;

const SYMBOLS = [
  '·', '■', '▲', '●', '◆', '★', '✚', '✕', '▼', '◑', '▣', '◒', '⬟', '❖', '◍', '⬤',
  '◧', '◨', '◩', '◪', '⯃', '⯄', '⌂', '☂', '☘', '❀', '✿', '❄', '✻', '✽', '⚑', '⚙',
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'P', 'R', 'S',
  'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '1', '2', '3', '4', '5', '6', '7', '8', '9',
];

export function symbolForIndex(i: number): string {
  if (i === EMPTY) return '';
  return SYMBOLS[i % SYMBOLS.length];
}

export function newId(): string {
  return `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

export interface NewPatternOptions {
  name?: string;
  stitch?: StitchId;
  beadSize?: BeadSizeId;
  width?: number;
  height?: number;
  palette?: BeadColor[];
  oddCount?: boolean;
}

export function createPattern(opts: NewPatternOptions = {}): Pattern {
  const width = clampDim(opts.width ?? 31);
  const height = clampDim(opts.height ?? 40);
  const now = Date.now();
  return {
    id: newId(),
    name: opts.name ?? 'Untitled pattern',
    stitch: opts.stitch ?? 'loom',
    beadSize: opts.beadSize ?? 'delica11',
    width,
    height,
    palette: [EMPTY_COLOR, ...(opts.palette ?? [])],
    cells: new Uint16Array(width * height),
    oddCount: opts.oddCount ?? false,
    meta: { author: '', notes: '', created: now, modified: now },
  };
}

export function clampDim(n: number): number {
  return Math.max(1, Math.min(MAX_DIM, Math.round(n || 1)));
}

export function idx(p: Pattern, col: number, row: number): number {
  return row * p.width + col;
}

export function inBounds(p: Pattern, col: number, row: number): boolean {
  return col >= 0 && row >= 0 && col < p.width && row < p.height;
}

export function getCell(p: Pattern, col: number, row: number): number {
  if (!inBounds(p, col, row)) return EMPTY;
  return p.cells[idx(p, col, row)];
}

export function clonePattern(p: Pattern): Pattern {
  return {
    ...p,
    palette: p.palette.map((c) => ({ ...c })),
    cells: new Uint16Array(p.cells),
    meta: { ...p.meta },
  };
}

export function touch(p: Pattern): Pattern {
  p.meta.modified = Date.now();
  return p;
}

/** Index of this colour in the pattern palette, adding it if it is new. */
export function ensureColor(p: Pattern, color: BeadColor): number {
  const existing = p.palette.findIndex(
    (c) => c.id === color.id || (c.hex.toLowerCase() === color.hex.toLowerCase() && c.code === color.code),
  );
  if (existing >= 0) return existing;
  p.palette.push({ ...color });
  return p.palette.length - 1;
}

/** Remove a palette entry; any cells using it become empty. */
export function removeColorAt(p: Pattern, index: number): Pattern {
  if (index <= EMPTY || index >= p.palette.length) return p;
  const next = clonePattern(p);
  next.palette.splice(index, 1);
  for (let i = 0; i < next.cells.length; i++) {
    const v = next.cells[i];
    if (v === index) next.cells[i] = EMPTY;
    else if (v > index) next.cells[i] = v - 1;
  }
  return touch(next);
}

/** Repaint every bead of colour `from` as colour `to`. */
export function swapColor(p: Pattern, from: number, to: number): Pattern {
  const next = clonePattern(p);
  for (let i = 0; i < next.cells.length; i++) if (next.cells[i] === from) next.cells[i] = to;
  return touch(next);
}

export function colorCounts(p: Pattern): Map<number, number> {
  const counts = new Map<number, number>();
  for (let i = 0; i < p.cells.length; i++) {
    const v = p.cells[i];
    if (v === EMPTY) continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return counts;
}

/** Drop palette entries that no beads use. */
export function compactPalette(p: Pattern): Pattern {
  const counts = colorCounts(p);
  const keep = p.palette.map((_, i) => i === EMPTY || counts.has(i));
  if (keep.every(Boolean)) return p;
  const remap = new Map<number, number>();
  const palette: BeadColor[] = [];
  p.palette.forEach((c, i) => {
    if (!keep[i]) return;
    remap.set(i, palette.length);
    palette.push(c);
  });
  const next = clonePattern(p);
  next.palette = palette;
  for (let i = 0; i < next.cells.length; i++) next.cells[i] = remap.get(next.cells[i]) ?? EMPTY;
  return touch(next);
}

export type Anchor =
  | 'top-left' | 'top' | 'top-right'
  | 'left' | 'center' | 'right'
  | 'bottom-left' | 'bottom' | 'bottom-right';

export function resizePattern(p: Pattern, width: number, height: number, anchor: Anchor = 'center'): Pattern {
  const w = clampDim(width);
  const h = clampDim(height);
  const cells = new Uint16Array(w * h);
  const dxTotal = w - p.width;
  const dyTotal = h - p.height;
  const dx = anchor.includes('left') ? 0 : anchor.includes('right') ? dxTotal : Math.floor(dxTotal / 2);
  const dy = anchor.startsWith('top') ? 0 : anchor.startsWith('bottom') ? dyTotal : Math.floor(dyTotal / 2);

  for (let r = 0; r < p.height; r++) {
    const nr = r + dy;
    if (nr < 0 || nr >= h) continue;
    for (let c = 0; c < p.width; c++) {
      const nc = c + dx;
      if (nc < 0 || nc >= w) continue;
      cells[nr * w + nc] = p.cells[r * p.width + c];
    }
  }
  const next = clonePattern(p);
  next.width = w;
  next.height = h;
  next.cells = cells;
  return touch(next);
}

export function flipHorizontal(p: Pattern): Pattern {
  const next = clonePattern(p);
  for (let r = 0; r < p.height; r++) {
    for (let c = 0; c < p.width; c++) {
      next.cells[r * p.width + c] = p.cells[r * p.width + (p.width - 1 - c)];
    }
  }
  return touch(next);
}

export function flipVertical(p: Pattern): Pattern {
  const next = clonePattern(p);
  for (let r = 0; r < p.height; r++) {
    for (let c = 0; c < p.width; c++) {
      next.cells[r * p.width + c] = p.cells[(p.height - 1 - r) * p.width + c];
    }
  }
  return touch(next);
}

/**
 * Quarter turn. Only offered for straight-grid stitches: rotating a staggered
 * chart would move beads to positions the stitch cannot produce.
 */
export function rotate90(p: Pattern, clockwise = true): Pattern {
  const w = p.height;
  const h = p.width;
  const cells = new Uint16Array(w * h);
  for (let r = 0; r < p.height; r++) {
    for (let c = 0; c < p.width; c++) {
      const v = p.cells[r * p.width + c];
      const nr = clockwise ? c : p.width - 1 - c;
      const nc = clockwise ? p.height - 1 - r : r;
      cells[nr * w + nc] = v;
    }
  }
  const next = clonePattern(p);
  next.width = w;
  next.height = h;
  next.cells = cells;
  return touch(next);
}

export function shiftPattern(p: Pattern, dx: number, dy: number, wrap = true): Pattern {
  const next = clonePattern(p);
  next.cells.fill(EMPTY);
  for (let r = 0; r < p.height; r++) {
    for (let c = 0; c < p.width; c++) {
      let nr = r + dy;
      let nc = c + dx;
      if (wrap) {
        nr = ((nr % p.height) + p.height) % p.height;
        nc = ((nc % p.width) + p.width) % p.width;
      } else if (nr < 0 || nr >= p.height || nc < 0 || nc >= p.width) {
        continue;
      }
      next.cells[nr * p.width + nc] = p.cells[r * p.width + c];
    }
  }
  return touch(next);
}

export function clearPattern(p: Pattern): Pattern {
  const next = clonePattern(p);
  next.cells.fill(EMPTY);
  return touch(next);
}

/** Fill every empty cell with the given colour index. */
export function fillBackground(p: Pattern, colorIndex: number): Pattern {
  const next = clonePattern(p);
  for (let i = 0; i < next.cells.length; i++) if (next.cells[i] === EMPTY) next.cells[i] = colorIndex;
  return touch(next);
}

/** Tightest rectangle containing every non-empty bead, or null if blank. */
export function contentBounds(p: Pattern): { x: number; y: number; w: number; h: number } | null {
  let minC = p.width;
  let minR = p.height;
  let maxC = -1;
  let maxR = -1;
  for (let r = 0; r < p.height; r++) {
    for (let c = 0; c < p.width; c++) {
      if (p.cells[r * p.width + c] === EMPTY) continue;
      if (c < minC) minC = c;
      if (c > maxC) maxC = c;
      if (r < minR) minR = r;
      if (r > maxR) maxR = r;
    }
  }
  if (maxC < 0) return null;
  return { x: minC, y: minR, w: maxC - minC + 1, h: maxR - minR + 1 };
}

export function cropTo(p: Pattern, box: { x: number; y: number; w: number; h: number }): Pattern {
  const w = clampDim(box.w);
  const h = clampDim(box.h);
  const cells = new Uint16Array(w * h);
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      const sr = box.y + r;
      const sc = box.x + c;
      if (sr < 0 || sr >= p.height || sc < 0 || sc >= p.width) continue;
      cells[r * w + c] = p.cells[sr * p.width + sc];
    }
  }
  const next = clonePattern(p);
  next.width = w;
  next.height = h;
  next.cells = cells;
  return touch(next);
}
